import { CheckoutSession } from '@nemuru-eng/js-api-client';
import { useEffect } from 'react';

import { useCheckoutSessionContext } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/contexts/CheckoutSessionContext';
import { useDialogSteps } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/hooks/useDialogSteps';
import { CartChangesReview } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Header/CheckoutSessionActions/components/EditCheckoutSessionCartDialog/components/steps/CartChangesReview/CartChangesReview';
import { CartEdit } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Header/CheckoutSessionActions/components/EditCheckoutSessionCartDialog/components/steps/CartEdit/CartEdit';
import { ConfirmNewAvailablePaymentMethods } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Header/CheckoutSessionActions/components/EditCheckoutSessionCartDialog/components/steps/ConfirmNewAvailablePaymentMethods/ConfirmNewAvailablePaymentMethods';
import { useGetNewAvailablePaymentMethods } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Header/CheckoutSessionActions/components/EditCheckoutSessionCartDialog/hooks/useGetNewAvailablePaymentMethods';
import { useApplicationContext } from '@/contexts/ApplicationContext/ApplicationContext';

interface EditCheckoutSessionCartStepsProps {
  checkoutSession: CheckoutSession;
  onComplete: () => void;
  onCancel: () => void;
}

export const EditCheckoutSessionCartSteps = ({
  checkoutSession,
  onComplete,
  onCancel,
}: EditCheckoutSessionCartStepsProps) => {
  const { paymentMethodDetailEntities } = useCheckoutSessionContext();
  const {
    state: { cart, downPayment },
  } = useApplicationContext();
  const { step, onPrev, onNext } = useDialogSteps({
    steps: Object.values(EditCheckoutSessionCartStep),
    onComplete,
    onCancel,
  });

  const {
    getNewAvailablePaymentMethods,
    setNewAvailablePaymentMethods,
    loadingGetNewAvailablePaymentMethods,
    newAvailablePaymentMethods,
    hasChangesInAvailablePaymentMethods,
  } = useGetNewAvailablePaymentMethods(checkoutSession, paymentMethodDetailEntities);

  useEffect(() => {
    if (hasChangesInAvailablePaymentMethods === null) return;
    if (step !== EditCheckoutSessionCartStep.CHANGES_REVIEW) return;

    completeOrShowNewAvailablePaymentMethods();
  }, [hasChangesInAvailablePaymentMethods]);

  useEffect(() => {
    setNewAvailablePaymentMethods(null);
  }, [cart?.total()]);

  const handleConfirmChanges = async () => {
    if (!cart) {
      throw new Error('cart not found');
    }

    if (newAvailablePaymentMethods) {
      completeOrShowNewAvailablePaymentMethods();
    } else {
      await getNewAvailablePaymentMethods(cart.total() - cart.totalDiscount() - downPayment.amount.amount);
    }
  };

  const completeOrShowNewAvailablePaymentMethods = async () => {
    if (hasChangesInAvailablePaymentMethods) {
      onNext();
    } else {
      onComplete();
    }
  };

  return (
    <>
      {step === EditCheckoutSessionCartStep.EDIT && <CartEdit onNext={onNext} onPrev={onPrev} />}
      {step === EditCheckoutSessionCartStep.CHANGES_REVIEW && (
        <CartChangesReview
          onNext={handleConfirmChanges}
          onPrev={onPrev}
          loading={loadingGetNewAvailablePaymentMethods}
        />
      )}
      {newAvailablePaymentMethods &&
        hasChangesInAvailablePaymentMethods &&
        step === EditCheckoutSessionCartStep.CONFIRM_NEW_AVAILABLE_PAYMENT_METHODS && (
          <ConfirmNewAvailablePaymentMethods
            onNext={onComplete}
            onPrev={onPrev}
            paymentMethodDetailEntities={paymentMethodDetailEntities}
            newAvailablePaymentMethods={newAvailablePaymentMethods}
          />
        )}
    </>
  );
};

export enum EditCheckoutSessionCartStep {
  EDIT = 'edit',
  CHANGES_REVIEW = 'changes_review',
  CONFIRM_NEW_AVAILABLE_PAYMENT_METHODS = 'confirm_new_available_payment_methods',
}
